import React, { Suspense } from "react";
import { Routes, Route, useParams } from "react-router-dom";
import { routes } from "./routesConfig";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { courseData } from "../components/teachersdashboard/data"; // For fallback data

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Special component to handle dynamic registration forms
function RegisterForm() {
  const { role } = useParams();
  const TeacherRegisterForm = React.lazy(() =>
    import("../components/Register/tutors/TeacherRegisterForm")
  );
  const OrganizationRegistrationForm = React.lazy(() =>
    import("../components/Register/organization/OrganisationRegistration")
  );
  const StudentRegisterForm = React.lazy(() =>
    import("../components/Register/student/StudentRegisterForm")
  );

  const renderForm = () => {
    switch (role) {
      case "teacher":
        return <TeacherRegisterForm paddingTop={16} />;
      case "organization":
        return <OrganizationRegistrationForm />;
      case "student":
        return <StudentRegisterForm paddingTop={20} />;
      default:
        return <StudentRegisterForm paddingTop={24} />;
    }
  };

  return <Suspense fallback={<Loading />}>{renderForm()}</Suspense>;
}

// Stripe setup
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

// Wrapper component for Stripe Elements
const StripeWrapper = ({ children }) => {
  return <Elements stripe={stripePromise}>{children}</Elements>;
};

const Router = () => {
  // Helper function to render routes recursively
  const renderRoutes = (routeConfig) => {
    return routeConfig.map((route) => {
      const RouteElement = route.element;

      // Handle nested routes
      if (route.children) {
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <Suspense fallback={<Loading />}>
                <RouteElement />
              </Suspense>
            }>
            {route.children.map((childRoute) => {
              const ChildElement = childRoute.element;

              if (childRoute.index) {
                return (
                  <Route
                    key="index"
                    index
                    element={
                      <Suspense fallback={<Loading />}>
                        <ChildElement />
                      </Suspense>
                    }
                  />
                );
              }

              if (childRoute.element === "REGISTER_FORM") {
                return (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={
                      <Suspense fallback={<Loading />}>
                        <RegisterForm />
                      </Suspense>
                    }
                  />
                );
              }

              // Handle Stripe Elements wrapper for child routes
              if (childRoute.wrapWithElements) {
                return (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={
                      <Suspense fallback={<Loading />}>
                        <StripeWrapper>
                          <ChildElement />
                        </StripeWrapper>
                      </Suspense>
                    }
                  />
                );
              }

              return (
                <Route
                  key={childRoute.path}
                  path={childRoute.path}
                  element={
                    <Suspense fallback={<Loading />}>
                      <ChildElement />
                    </Suspense>
                  }
                />
              );
            })}
          </Route>
        );
      }

      // Handle Stripe Elements wrapper for top-level routes
      if (route.wrapWithElements) {
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <Suspense fallback={<Loading />}>
                <StripeWrapper>
                  <RouteElement />
                </StripeWrapper>
              </Suspense>
            }
          />
        );
      }

      // Handle components that need props
      if (route.path === "course-details") {
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <Suspense fallback={<Loading />}>
                <RouteElement course={courseData} />
              </Suspense>
            }
          />
        );
      }

      // Handle standard routes
      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            <Suspense fallback={<Loading />}>
              <RouteElement />
            </Suspense>
          }
        />
      );
    });
  };

  return <Routes>{renderRoutes(routes)}</Routes>;
};

export default Router;
